// @flow

import { v4 } from 'uuid';
import React, { useMemo } from 'react';
import type { Node } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';
import { Section, SectionContent, Wrapper } from '@riseart/layout';
import { MediaQuery, VerticalMenu, Link } from '@riseart/common';
import { HTTP_STATUS, pagination as ENUM_PAGINATION } from 'Enum';
import { useQuery } from 'shared_services/apollo/useQuery';
import { useUrlParams } from 'shared_data/providers/url/useUrlParams';
import { localeSelector } from 'shared_services/redux/selectors/locale';
import { useStoreCode } from 'shared_services/redux/hooks/useStoreCode';
import { LayoutDefault } from 'shared_components/layouts/Default';
import { ArtistBreadcrumbs } from 'shared_components/artist/breadcrumbs/Breadcrumbs';
import { ArtistHeader } from 'shared_components/artist/header/Header';
import { IsomorphicRipple } from 'shared_components/common/preloader/IsomorphicRipple';
import { VerticalMenuOverlay } from 'shared_components/common/menu/overlay/Menu';
import { ArtistDetailMeta } from 'shared_components/artist/detail/Meta';
import { DataProviderPreloaderPage } from 'shared_components/data/providers/preloaders/Page';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { gql404RedirectHandler } from 'shared_services/riseart/utils/RouteUtils';
import { FollowButton } from 'shared_components/common/follow/Button';
import { FilterFollowsProvider } from 'shared_data/providers/follow/Follow';
import { ArtistShare } from 'shared_components/artist/share/Share';
import { CreateFollowProvider } from 'shared_data/providers/follow/CreateFollow';
import { HOCGridPaginated } from 'shared_hocs/common/grid/Paginated';
import { ActivityList } from 'shared_components/activity/List';
import { ArtistNews as ArtistNewsSection } from 'shared_components/artist/News';
import { MENU_ITEMS, mapArtistPageData } from 'shared_services/riseart/utils/artist';
import DETAIL_ARTIST from 'shared_data/queries/artist/detail.graphql';
import LIST_ACTIVITIES from 'shared_data/queries/artist/listActivities.graphql';

import { raScreenLg } from '@riseart/antd-provider/dist/website/variables.less';
import { artistHeaderSectionCls } from 'shared_components/artist/header/Header.less';

type Props = { staticContext: ?Object };

const PRELOADER_SUBSCRIBER_ID = v4();
const ActivityFeedGrid = HOCGridPaginated(ActivityList);

/**
 * ArtistNewsPage
 */
export default function ArtistNewsPage({ staticContext }: Props): Node {
  const { id: artistId } = useParams();
  const history = useHistory();
  const { translatedLocation: location } = useUrlParams();
  const currentLocale = useSelector(localeSelector, shallowEqual);
  const { formatMessage } = useIntl();
  const storeCode = useStoreCode();
  const { loading, data, error } = useQuery(DETAIL_ARTIST, {
    variables: { id: artistId },
    context: {
      customOptions: {
        errorSuppressFromResponse: true,
        errorHandler: gql404RedirectHandler(
          UrlAssembler.artistList({
            search: { message: HTTP_STATUS.NOT_FOUND },
            locale: currentLocale,
          }),
          history,
          staticContext,
        ),
      },
    },
  });
  const { detailArtist: artistData } = data || {};
  const {
    hasError,
    canonicalURL,
    subpageCanonicalRedirect,
    hrefLangRedirects,
    artistUrl,
    name,
    artistMenuItems,
    artistImages: { mainBannerImage },
  } = useMemo(
    () =>
      mapArtistPageData(artistData, error, MENU_ITEMS.news, 'artistNews', {
        formatMessage,
        locale: currentLocale,
        location,
      }),
    [artistData, error, formatMessage, currentLocale, location],
  );

  return (
    <LayoutDefault scrollTopOnPathChange>
      <DataProviderPreloaderPage uniqueId={PRELOADER_SUBSCRIBER_ID} attach={loading} />
      {loading && <IsomorphicRipple isActive isFullScreen />}
      {artistData ? (
        <React.Fragment>
          {/* Meta Provider */}
          <ArtistDetailMeta
            hasError={hasError}
            artistData={artistData}
            mainBannerImage={mainBannerImage}
            canonicalURL={canonicalURL}
            subpageCanonicalRedirect={subpageCanonicalRedirect}
            hrefLangRedirects={hrefLangRedirects}
            formatMessage={formatMessage}
            translationMetas={{ metaTitle: true }}
            translationKeyPrefix="meta.artistNews"
            noIndex
          />
          <Section className={artistHeaderSectionCls}>
            <Wrapper>
              <ArtistBreadcrumbs
                pageLabel={MENU_ITEMS.news}
                id={artistData.id}
                alias={artistData.alias}
                name={name}
                mediums={artistData.mediums}
                styles={artistData.styles}
              />
            </Wrapper>
            <FormattedMessage id="components.artist.news">
              {(title: string) => (
                <ArtistHeader
                  title={title}
                  subtitle={
                    artistUrl ? (
                      <Link to={artistUrl} title={name}>
                        {name}
                      </Link>
                    ) : (
                      name
                    )
                  }
                >
                  <FilterFollowsProvider>
                    <CreateFollowProvider leadersList={artistData} />
                    <FollowButton leaderId={artistData.id} leaderData={artistData} />
                  </FilterFollowsProvider>
                  <ArtistShare name={name} image={mainBannerImage} url={canonicalURL} />
                  <MediaQuery minWidth={raScreenLg}>
                    {(isScreenLg) =>
                      isScreenLg ? (
                        <VerticalMenu disableFixedMenu absolutePosition items={artistMenuItems} />
                      ) : (
                        <VerticalMenuOverlay title={name} items={artistMenuItems} />
                      )
                    }
                  </MediaQuery>
                </ArtistHeader>
              )}
            </FormattedMessage>
          </Section>
          <ArtistNewsSection artistId={artistId} />
          <Section>
            <Wrapper
              sizes={{
                xs: 'wide',
                sm: 'wide',
                md: 'wide',
                lg: 'narrow',
                xl: 'narrow',
              }}
            >
              <SectionContent
                nodeType="div"
                title={
                  <FormattedMessage id="components.artist.recentActivity">
                    {(title: string) => title}
                  </FormattedMessage>
                }
                headingProps={{ tag: 'h2', align: 'left' }}
              >
                <ActivityFeedGrid
                  fetchQuery={LIST_ACTIVITIES}
                  variables={{
                    artistId,
                    store: storeCode,
                    page: 1,
                    items: 10,
                  }}
                  ssrQuery={false}
                  skipQuery={!storeCode}
                  autoLoading={false}
                  paginationType={ENUM_PAGINATION.type.INFINITE}
                />
              </SectionContent>
            </Wrapper>
          </Section>
        </React.Fragment>
      ) : null}
    </LayoutDefault>
  );
}
